import { fireflyArtwork } from './58-firefly-artwork.mjs';
import { bubbleArtwork } from './211-bubble-materials.mjs';

let serial = 0;
const paradePicture = new URL('../assets/game-pocket-parade-v1-640.webp', import.meta.url).href;
const memoryAtlas = new URL('../assets/game-hidden-friends-characters-v1-640.webp', import.meta.url)
  .href;
const memoryNames = Object.freeze([
  'Fox',
  'Penguin',
  'Frog',
  'Rabbit',
  'Lion',
  'Pig',
  'Turtle',
  'Kitten',
]);

/** Name only an authorized visible memory projection. Hidden, malformed or
 * out-of-atlas values return null; this helper never reads a puzzle's answers. */
export function visibleMemoryName(cell) {
  return Number.isInteger(cell?.value) && cell.value >= 0 && cell.value < memoryNames.length
    ? memoryNames[cell.value]
    : null;
}

function gradient(ctx, art, kit, stops, radial = false) {
  const id = `score-material-${++serial}`;
  const defs = kit.add(ctx, art, 'defs', {});
  const paint = kit.add(ctx, defs, radial ? 'radialGradient' : 'linearGradient', {
    id,
    ...(radial
      ? { cx: '31%', cy: '23%', r: '80%' }
      : { x1: '12%', y1: '0%', x2: '80%', y2: '100%' }),
  });
  for (const [offset, colour] of stops)
    kit.add(ctx, paint, 'stop', { offset, 'stop-color': colour });
  return `url(#${id})`;
}
function number(ctx, art, kit, value, fill, size = 43) {
  kit.add(
    ctx,
    art,
    'text',
    {
      x: 50,
      y: 54,
      fill,
      'text-anchor': 'middle',
      'dominant-baseline': 'middle',
      'font-family': 'ui-rounded, system-ui, sans-serif',
      'font-size': size,
      'font-weight': 850,
      'letter-spacing': '-1',
    },
    String(value),
  );
}
function rect(ctx, art, kit, attributes) {
  return kit.add(ctx, art, 'rect', { x: 7, y: 7, width: 86, height: 86, rx: 13, ...attributes });
}
function merge(ctx, cell, kit) {
  const art = kit.svg(ctx, 'game-piece-art game-merge-cube');
  if (!cell.value) {
    rect(ctx, art, kit, {
      fill: 'var(--game-night)',
      stroke: 'var(--game-neutral)',
      'stroke-width': 2,
    });
    return art;
  }
  const power = Math.max(1, Math.round(Math.log2(cell.value)));
  art.setAttribute('data-merge-power', String(power));
  art.setAttribute('data-merge-value', String(cell.value));
  const hue =
    power >= 10
      ? 'color-mix(in srgb,var(--piece-colour) 72%,var(--game-colour-3))'
      : 'var(--piece-colour)';
  const paint = gradient(ctx, art, kit, [
    ['0%', `color-mix(in srgb,${hue} 72%,var(--game-white))`],
    ['28%', hue],
    ['68%', `color-mix(in srgb,${hue} 82%,var(--game-night))`],
    ['100%', `color-mix(in srgb,${hue} 45%,var(--game-night))`],
  ]);
  const face = gradient(ctx, art, kit, [
    ['0%', `color-mix(in srgb,${hue} 75%,var(--game-white))`],
    ['48%', hue],
    ['100%', `color-mix(in srgb,${hue} 64%,var(--game-night))`],
  ]);
  kit.path(ctx, art, 'M16 7H84L95 18V82L84 93H16L5 82V18Z', {
    fill: paint,
    stroke: 'color-mix(in srgb,var(--piece-colour) 65%,var(--game-white))',
    'stroke-width': 1.5,
  });
  kit.path(ctx, art, 'M16 7H84L76 19H24L5 18Z', { fill: 'var(--game-white)', opacity: 0.65 });
  kit.path(ctx, art, 'M5 18L24 19V74L16 93L5 82Z', { fill: 'var(--game-white)', opacity: 0.23 });
  kit.path(ctx, art, 'M24 74H76L95 82L84 93H16Z', { fill: 'var(--game-night)', opacity: 0.48 });
  rect(ctx, art, kit, {
    x: 19,
    y: 19,
    width: 62,
    height: 62,
    rx: 9,
    fill: face,
    stroke: 'var(--game-glass)',
    'stroke-width': 1,
  });
  kit.path(ctx, art, 'M28 23H72Q77 23 77 28V33L24 44V28Q24 23 28 23Z', {
    fill: 'var(--game-white)',
    opacity: 0.24,
  });
  kit.path(ctx, art, 'M23 72Q23 78 29 78H72', {
    fill: 'none',
    stroke: 'var(--game-night)',
    'stroke-width': 2,
    opacity: 0.35,
  });
  if (power >= 8) {
    kit.path(ctx, art, 'M84 8L91 15L84 22L77 15Z', {
      fill: 'var(--game-colour-3)',
      stroke: 'var(--game-white)',
      'stroke-width': 0.8,
    });
    for (const [x, y, radius] of [
      [16, 15, 6],
      [84, 81, 4],
    ])
      kit.path(
        ctx,
        art,
        `M${x} ${y - radius}L${x + 1.5} ${y - 1.5}L${x + radius} ${y}L${x + 1.5} ${y + 1.5}L${x} ${y + radius}L${x - 1.5} ${y + 1.5}L${x - radius} ${y}L${x - 1.5} ${y - 1.5}Z`,
        {
          class: 'game-merge-twinkle',
          fill: 'var(--game-white)',
          opacity: 0.9,
        },
      );
  }
  number(
    ctx,
    art,
    kit,
    cell.value,
    'var(--game-white)',
    cell.value >= 1000 ? 25 : cell.value >= 100 ? 34 : 43,
  );
  // The painted glyph remains the full accepted value; bevels never substitute a number.

  return art;
}
function paradeTile(ctx, cell, kit) {
  const art = kit.svg(ctx, 'game-piece-art game-slider-art game-picture-tile');
  if (!Number.isInteger(cell.value) || cell.value < 1 || cell.value > 8) {
    rect(ctx, art, kit, {
      x: 1,
      y: 1,
      width: 98,
      height: 98,
      fill: 'var(--game-night)',
      stroke: 'var(--game-line)',
    });
    return art;
  }
  art.setAttribute('data-tile-piece', String(cell.value));
  const position = cell.value - 1;
  const viewport = kit.add(ctx, art, 'svg', {
    x: 1,
    y: 1,
    width: 98,
    height: 98,
    viewBox: `${(position % 3) * 100} ${Math.floor(position / 3) * 100} 100 100`,
    preserveAspectRatio: 'xMidYMid meet',
    overflow: 'hidden',
  });
  // One square painting is partitioned by the accepted tile identity, so a
  // solved board restores the original composition without per-tile cropping.
  kit.add(ctx, viewport, 'image', {
    x: 0,
    y: 0,
    width: 300,
    height: 300,
    href: paradePicture,
    preserveAspectRatio: 'xMidYMid meet',
  });
  rect(ctx, art, kit, {
    x: 1,
    y: 1,
    width: 98,
    height: 98,
    rx: 4,
    fill: 'none',
    stroke: 'var(--game-glass)',
    'stroke-width': 2,
  });
  kit.add(ctx, art, 'rect', {
    x: 71,
    y: 5,
    width: 24,
    height: 25,
    rx: 7,
    fill: 'var(--game-night)',
    'fill-opacity': 0.94,
    stroke: 'var(--game-white)',
    'stroke-width': 0.8,
  });
  kit.add(
    ctx,
    art,
    'text',
    {
      x: 83,
      y: 23,
      fill: 'var(--game-white)',
      'font-family': 'ui-rounded, system-ui, sans-serif',
      'font-size': 19,
      'font-weight': 850,
      'text-anchor': 'middle',
    },
    String(cell.value),
  );
  return art;
}
function memory(ctx, cell, shape, kit) {
  const art = kit.svg(ctx, 'game-piece-art game-memory-art');
  // The hidden branch is deliberately identical for every card and never reads
  // the derived colour or glyph. No shape, material or attribute leaks an answer.
  const name = visibleMemoryName(cell);
  if (!name) {
    const paint = gradient(ctx, art, kit, [
      ['0%', 'color-mix(in srgb,var(--game-colour-0) 40%,var(--game-night))'],
      ['100%', 'var(--game-night)'],
    ]);
    rect(ctx, art, kit, { fill: paint, stroke: 'var(--game-colour-3)', 'stroke-width': 2 });
    rect(ctx, art, kit, {
      x: 13,
      y: 13,
      width: 74,
      height: 74,
      rx: 8,
      fill: 'none',
      stroke: 'var(--game-colour-3)',
      'stroke-width': 0.8,
      opacity: 0.65,
    });
    for (let petal = 0; petal < 4; petal++)
      kit.path(ctx, art, 'M50 50Q19 20 50 24Q81 20 50 50Z', {
        fill: 'none',
        stroke: 'var(--game-colour-3)',
        'stroke-width': 1.5,
        opacity: 0.8,
        transform: `rotate(${petal * 90} 50 50)`,
      });
    kit.circle(ctx, art, 50, 50, 5, { fill: 'var(--game-colour-3)' });
    kit.path(ctx, art, 'M50 66V78M50 73Q32 58 31 72Q40 80 50 73M50 73Q68 58 69 72Q60 80 50 73', {
      fill: 'none',
      stroke: 'var(--game-colour-3)',
      'stroke-width': 2,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
    });
    return art;
  }
  const paint = gradient(ctx, art, kit, [
    ['0%', 'var(--game-white)'],
    ['100%', 'var(--game-paper)'],
  ]);
  rect(ctx, art, kit, { fill: paint, stroke: 'var(--piece-colour)', 'stroke-width': 3 });
  rect(ctx, art, kit, {
    x: 13,
    y: 13,
    width: 74,
    height: 74,
    rx: 8,
    fill: 'none',
    stroke: 'var(--piece-colour)',
    'stroke-width': 1,
  });
  const mark = kit.add(ctx, art, 'g', {
    transform: 'translate(-6 -6) scale(1.12)',
    'data-memory-fallback': 'true',
  });
  kit.symbol(ctx, mark, cell.glyph || shape, 'var(--piece-colour)');
  const viewport = kit.add(ctx, art, 'svg', {
    x: 9,
    y: 9,
    width: 82,
    height: 82,
    viewBox: `${(cell.value % 4) * 100} ${Math.floor(cell.value / 4) * 100} 100 100`,
    preserveAspectRatio: 'xMidYMid meet',
    overflow: 'hidden',
  });
  const image = kit.add(ctx, viewport, 'image', {
    x: 0,
    y: 0,
    width: 400,
    height: 200,
    href: memoryAtlas,
    preserveAspectRatio: 'xMidYMid meet',
  });
  image.addEventListener?.('load', () => mark.setAttribute('visibility', 'hidden'), { once: true });
  image.addEventListener?.(
    'error',
    () => {
      mark.setAttribute('visibility', 'visible');
      viewport.remove();
    },
    { once: true },
  );
  art.setAttribute('data-memory-character', name);
  for (const [x, y] of [
    [22, 22],
    [78, 78],
  ])
    kit.circle(ctx, art, x, y, 3, { fill: 'var(--piece-colour)' });
  if (cell.matched)
    kit.path(ctx, art, 'M39 78L47 84L63 70', {
      fill: 'none',
      stroke: 'var(--game-paper-ink)',
      'stroke-width': 3,
    });
  return art;
}
function trinket(ctx, cell, shape, kit) {
  const art = kit.svg(ctx, 'game-piece-art game-trinket-art');
  const glyph = cell.glyph || shape;
  const scale = glyph === '●' ? 2.25 : glyph === '■' ? 2 : glyph === '★' ? 1.55 : 1.7;
  const silhouette = `translate(50 50) scale(${scale}) translate(-50 -50)`;
  const paint = gradient(ctx, art, kit, [
    ['0%', 'var(--game-white)'],
    ['12%', 'color-mix(in srgb,var(--piece-colour) 75%,var(--game-white))'],
    ['28%', 'var(--piece-colour)'],
    ['62%', 'var(--piece-colour)'],
    ['82%', 'color-mix(in srgb,var(--piece-colour) 48%,var(--game-night))'],
    ['100%', 'color-mix(in srgb,var(--piece-colour) 75%,var(--game-white))'],
  ]);
  kit.add(ctx, art, 'ellipse', { cx: 51, cy: 87, rx: 30, ry: 5, fill: 'var(--game-shadow)' });
  const shadow = kit.add(ctx, art, 'g', { transform: `translate(1 6) ${silhouette}` });
  kit.symbol(ctx, shadow, glyph, 'color-mix(in srgb,var(--piece-colour) 35%,var(--game-night))');
  const ceramic = kit.add(ctx, art, 'g', { transform: silhouette });
  kit.symbol(ctx, ceramic, glyph, 'var(--game-paper)');
  const enamel = kit.add(ctx, art, 'g', {
    transform: `translate(50 50) scale(.88) translate(-50 -50) ${silhouette}`,
  });
  kit.symbol(ctx, enamel, glyph, paint);
  const polish = kit.add(ctx, art, 'g', {
    transform: `translate(-2 -4) translate(50 50) scale(.72) translate(-50 -50) ${silhouette}`,
    opacity: 0.16,
  });
  kit.symbol(ctx, polish, glyph, 'var(--game-white)');
  art.setAttribute('data-symbol', glyph);
  art.setAttribute('data-trinket-value', String(cell.value));
  art.setAttribute('data-trinket-shape', glyph);
  kit.circle(ctx, art, 83, 84, 12, {
    fill: 'var(--game-paper)',
    stroke: 'var(--game-paper-ink)',
    'stroke-width': 1,
  });
  kit.add(
    ctx,
    art,
    'text',
    {
      x: 83,
      y: 89,
      fill: 'var(--game-paper-ink)',
      'text-anchor': 'middle',
      'font-size': 16,
      'font-weight': 800,
    },
    String(cell.colour + 1),
  );
  return art;
}
function pipes(ctx, cell, kit) {
  const art = kit.svg(ctx, 'game-piece-art game-pipe-art');
  art.setAttribute('data-lantern-lit', String(Boolean(cell.lit)));
  art.setAttribute(
    'data-lantern-role',
    cell.source ? 'source' : cell.destination ? 'destination' : 'relay',
  );
  const copper = gradient(ctx, art, kit, [
    ['0%', 'var(--game-white)'],
    ['18%', 'var(--game-colour-3)'],
    ['42%', 'var(--game-colour-1)'],
    ['58%', 'var(--game-colour-3)'],
    ['100%', 'color-mix(in srgb,var(--game-colour-1) 45%,var(--game-night))'],
  ]);
  const lens = gradient(
    ctx,
    art,
    kit,
    [
      ['0%', 'var(--game-white)'],
      ['23%', cell.lit ? 'var(--game-colour-0)' : 'var(--game-colour-4)'],
      ['62%', cell.lit ? 'var(--game-colour-0)' : 'var(--game-night)'],
      ['100%', 'var(--game-night)'],
    ],
    true,
  );
  for (const [bit, end] of [
    [1, '50 0'],
    [2, '100 50'],
    [4, '50 100'],
    [8, '0 50'],
  ]) {
    if (!(cell.value & bit)) continue;
    const d = `M50 50L${end}`;
    for (const [width, stroke, opacity, cls] of [
      [31, 'var(--game-night)', 1, 'lantern-channel-shadow'],
      [26, copper, 1, 'lantern-copper-rail'],
      [18, 'var(--game-night)', 1, 'lantern-glass-bed'],
      [12, cell.lit ? 'var(--game-colour-0)' : 'var(--game-night)', 1, 'lantern-current'],
      [3, 'var(--game-white)', cell.lit ? 0.75 : 0.12, 'lantern-reflection'],
    ])
      kit.path(ctx, art, d, {
        class: cls,
        fill: 'none',
        stroke,
        opacity,
        'stroke-width': width,
        'stroke-linecap': 'round',
      });
  }
  kit.circle(ctx, art, 50, 50, cell.source || cell.destination ? 23 : 17, {
    class: 'lantern-rim',
    fill: copper,
    stroke: 'var(--game-night)',
    'stroke-width': 2,
  });
  if (cell.destination)
    kit.path(ctx, art, 'M50 30L70 50L50 70L30 50Z', {
      class: 'lantern-lens',
      fill: lens,
      stroke: 'var(--game-white)',
      'stroke-width': 1,
      'stroke-opacity': 0.6,
    });
  else
    kit.circle(ctx, art, 50, 50, cell.source ? 18 : 12, {
      class: 'lantern-lens',
      fill: lens,
      stroke: 'var(--game-white)',
      'stroke-width': 1,
      'stroke-opacity': 0.5,
    });
  kit.path(ctx, art, 'M42 42Q46 37 54 40', {
    class: 'lantern-lens-highlight',
    fill: 'none',
    stroke: 'var(--game-white)',
    'stroke-width': 2.5,
    'stroke-linecap': 'round',
    opacity: 0.8,
  });
  if (cell.source)
    kit.circle(ctx, art, 50, 50, 5, {
      fill: 'var(--game-white)',
      opacity: cell.lit ? 0.9 : 0.4,
    });
  return art;
}

/** Original material artwork consumes only visible cell metadata. Shared SVG
 * primitives and actual glyphs stay owned by36; this module owns score families.
 * It changes no rule, input position, state, timer or hidden answer projection. */
export function scoreCellArtwork(ctx, gameId, cell, shape, kit) {
  switch (gameId) {
    case 'cluster-pop':
      return bubbleArtwork(ctx, cell, shape, kit);
    case 'soft-merge':
      return merge(ctx, cell, kit);
    case 'tile-slide':
      return paradeTile(ctx, cell, kit);
    case 'number-trail':
      return fireflyArtwork(ctx, cell, kit);
    case 'memory-pairs':
      return memory(ctx, cell, shape, kit);
    case 'spot-pair':
      return trinket(ctx, cell, shape, kit);
    case 'rotation-loop':
      return pipes(ctx, cell, kit);
    default:
      return null;
  }
}
